import { colors } from '@colors'
import type {
  AlignValue,
  LegendOptions,
  TooltipOptions,
  TooltipFormatterCallbackFunction,
  Point,
  VerticalAlignValue,
} from 'highcharts'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'

/**
 * Provides several functions for highcharts config.
 * offer highcharts data structure for typical
 * reporting labels, legends, plotlines, and tooltips.
 * Includes customisation options
 *
 * @module HighchartsConfig
 */

/**
 * Generate a HighChartsTitle config object for reporting.
 *
 * This function might seem pointless but it gives a place where we can adjust title options for all charts.
 * Currently used for both chart titles and axis titles. Though available options differ between them, so we may want
 * to refactor to add a new reportingAxisTitle() function for axis titles.
 *
 * @memberOf HighchartsConfig
 */
export function reportingTitle(text: string, options = {}) {
  return { text, ...options }
}
/**
 * Provides a highcharts data structure for a typical reporting legend.
 *
 * @memberOf HighchartsConfig
 * @param  {String} [horizontal='left'] - The horizontal alignment of the legend
 * @param  {String} [vertical='top'] - The vertical alignment of the legend
 * @param  {Number} [xOffset=50] - The xOffset of the legend. You'll typically
 *                                 change this if you're overlapping a label.
 * @param  {Number} [yOffset=0] - The yOffset of the legend. You'll typically
 *                                 change this if you've overlapping a label.
 * @param {Boolean} [options.enabled=true] - Is legend enabled
 * @param {Number} [options.symbolHeight=11] - Height of symbol
 * @param {Number} [options.symbolWidth=11] - Width of symbol
 * @param {Number} [options.symbolRadius=6] - Radius of symbol
 * @memberOf HighchartsConfig
 */

export function reportingLegend(
  horizontal: AlignValue = 'left',
  vertical: VerticalAlignValue = 'top',
  xOffset = 50,
  yOffset = 0,
  { enabled = true, symbolHeight = 11, symbolWidth = 11, symbolRadius = 6 }: LegendOptions = {},
): LegendOptions {
  const config = {
    enabled,
    align: horizontal,
    verticalAlign: vertical,
    x: xOffset,
    y: yOffset,
    symbolHeight,
    symbolWidth,
    symbolRadius,
  }
  return config
}

/**
 * Provides a highcharts data structure for a typical chart plot line. The line
 * is drawn at the position indicated by `value` and labelled by `label`. The
 * line and label may be styled by passing in an `options` object.
 *
 * @memberOf HighchartsConfig
 * @param {Object} [options] - The options object to override display properties of the plot line.
 * @param {String} [options.colour = '#aaa'] - CSS colour value to set the colour of the line and label.
 * @param {String} [options.fontSize = '80%'] - CSS font-size value to set the size of the label.
 * @param {String} [options.icon = '&#9650'] - HTML string to render an icon on the left of the string label.
 * @param {Number} [options.width = 1] - Width to draw the plot line.
 * @param {String} [options.align = 'right'] - Direction to align the plot line label.
 * @param {String} [options.textAlign = 'left'] - Direction to align the plot line label.
 * @param {Number} [options.x = 10] - Position the plot line label in x.
 * @param {Number} [options.x = 0] - Position the plot line label in y.
 *
 * @return {HighChartsPlotLine} Highcharts plot line configuration object.
 */

interface PlotLineOptions {
  color?: string
  labelColor?: string
  fontSize?: string
  icon?: string
  width?: number
  align?: AlignValue
  textAlign?: AlignValue
  x?: number
  y?: number
  rotation?: number
  zIndex?: number
}

export function reportingPlotLine(
  value: number,
  label: string,
  {
    color: color = colors.dustyBlack200,
    labelColor: labelColor,
    fontSize = '80%',
    icon = '&#9650;',
    width = 1,
    align = 'right',
    textAlign = 'left',
    x = 10,
    y = 0,
    rotation,
    zIndex = 0,
  }: PlotLineOptions = {},
): Highcharts.AxisPlotLinesOptions {
  const text = `${icon} ${label}`
  const config = {
    color,
    width,
    value,
    zIndex,
    label: {
      useHTML: true,
      align,
      textAlign,
      rotation,
      style: {
        color: labelColor || color,
        fontSize,
      },
      x,
      y,
      text,
    },
  }
  return config
}

/**
 * Provides a highcharts data structure for a typical chart tooltip. The tooltip
 * will display the value of the `value` property at the current point, folowed
 * by the `unit`. The appearance may be adjusted by passing in an `options` object.
 */

interface ReportingTooltipOptions {
  enabled?: boolean
  color?: string
  backgroundColor?: string
  borderWidth?: number
  borderRadius?: number
  shadow?: boolean
  formatter?: TooltipFormatterCallbackFunction
}

export function reportingToolTip(
  value: 'x' | 'y',
  unit = '',
  {
    enabled = true,
    color = colors.white,
    backgroundColor = colors.dustyBlack400,
    borderWidth = 0,
    borderRadius = 5,
    shadow = false,
    formatter = undefined,
  }: ReportingTooltipOptions = {},
): TooltipOptions {
  // Set here so we can use the 'value' and 'unit' params
  if (typeof formatter !== 'function') {
    formatter = function (this: Point) {
      const formattedValue = formatNumber(this[value] ?? 0, 2)
      return `<b>${formattedValue}${unit || ''}</b>`
    }
  }

  return {
    enabled,
    backgroundColor,
    useHTML: true,
    borderWidth,
    borderRadius,
    shadow,
    formatter,
    style: {
      color,
    },
  }
}
